/* Immediate booking (30 Oct 2026, mock AppCall_3, change 6). A trip starting within 3 hours:
   instead of calling one driver, the customer picks the seats (required) and a model (if
   they like), sees the fare and books; Sramika sends it to the matching drivers and the
   first to accept confirms it. Short and plain - the customer sees a booking, not the
   machinery behind it. In the reader's language. */

import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../utils/api";
import { getUserRole } from "../utils/auth";
import { useLanguage } from "../context/LanguageContext";
import { useLocalNames } from "../utils/localNames";
import { fillText } from "../utils/leadDisplay";
import { track } from "../utils/analytics";

// 8 hours since 4 Nov 2026 (mock TripPool_1); 3 from 30 Oct, 6 before that.
export const INSTANT_HOURS = 8;
/* Paused 30-Oct to 4-Nov 2026 until the trip-request template was approved; on again with
   TripPool_1. A trip within 8 hours gets the urgent note; Call on a car still calls that driver. */
export const INSTANT_ON = true;
/** Whether a pickup time is close enough for an immediate booking. */
export const isImmediate = (pickup) => {
  if (!INSTANT_ON) return false;
  const ms = Date.parse(pickup || "");
  return Number.isFinite(ms) && ms - Date.now() < INSTANT_HOURS * 3600000;
};
/** The seats a car offers, as the sheet's choice. */
export const seatClassOf = (car) => {
  const c = String(car?.category || "");
  if (/^(TEMPO_TRAVELLER|MINI_BUS|BUS)_/.test(c)) return "12";
  if (/_7(_|$)/.test(c)) return "7";
  return "5";
};
const SEATS = [["5", "ib_seat5"], ["7", "ib_seat7"], ["12", "ib_seat12"]];
const rupees = (n) => Math.round(Number(n) || 0).toLocaleString("en-IN");
const iso = (v) => { const ms = Date.parse(v || ""); return Number.isFinite(ms) ? new Date(ms).toISOString() : null; };

/* The one-click bar above the car list (30 Oct 2026): a confirmed booking with the car
   Sramika recommends, and the fare range of the cars that fit - for any trip. */
export function OneClickBookingBar({ trip, onOpen }) {
  const { t } = useLanguage();
  const [range, setRange] = useState(null);
  const key = JSON.stringify([trip.city, trip.from, trip.to, trip.pickupAt, trip.returnAt, trip.tripType]);
  useEffect(() => {
    let alive = true;
    setRange(null);
    if (!trip.from || !trip.to || !iso(trip.pickupAt) || !trip.city) return undefined;
    apiRequest("/trip-requests/quote", { method: "POST", body: JSON.stringify({ city: trip.city, from: trip.from, to: trip.to, pickupAt: iso(trip.pickupAt),
      returnAt: trip.tripType === "ROUND" ? iso(trip.returnAt) : null, tripType: trip.tripType }) })
      .then((r) => { if (alive) setRange(r?.data?.prices || null); }).catch(() => {});
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!range || !INSTANT_ON) return null;
  // Within 8 hours: the urgent note (mock TripPool_1, 1); any other time: "Urgent trip?" (2).
  if (isImmediate(trip.pickupAt)) return (
    <div className="w-full mt-2 rounded-2xl border-2 border-[#F59E0B] bg-gradient-to-br from-[#FFF7E6] to-[#FFFBEB] px-3.5 py-3">
      <p className="text-[14.5px] font-extrabold text-[#92400E]">{t("ib_urgentTitle")}</p>
      <p className="text-[12px] text-[#78350F] mt-0.5 leading-snug">{t("ib_urgentText")}</p>
      <p className="text-[12px] font-bold text-[#78350F] mt-1">₹{rupees(range[0])} – ₹{rupees(range[1])}</p>
      <button type="button" onClick={onOpen} className="w-full min-h-[44px] mt-2 rounded-xl bg-[#F59E0B] text-white text-[13px] font-extrabold active:scale-[0.99]">{t("ib_urgentBtn")}</button>
    </div>
  );
  return (
    <button type="button" onClick={onOpen}
      className="w-full mt-2 rounded-2xl border-2 border-[#FCD34D] bg-[#FFFBEB] px-3.5 py-3 flex items-center justify-between gap-3 text-left active:scale-[0.99]">
      <span className="min-w-0">
        <span className="block text-[14px] font-extrabold text-[#92400E]">{t("ob_title")}</span>
        <span className="block text-[12px] text-[#78350F] font-bold mt-0.5">{fillText(t("ob_sub"), { min: rupees(range[0]), max: rupees(range[1]) })}</span>
      </span>
      <span className="shrink-0 rounded-xl bg-[#F59E0B] text-white text-[12.5px] font-extrabold px-3.5 py-2.5">{t("ib_book")}</span>
    </button>
  );
}

export default function InstantBookingSheet({ trip, initialSeats = "", initialModel = "", mode = "instant", onClose, onLogin }) {
  const { t, lang } = useLanguage();
  const ln = useLocalNames();
  const [seats, setSeats] = useState(initialSeats);
  const [model, setModel] = useState(initialModel);
  const [quote, setQuote] = useState(null);
  const [stage, setStage] = useState("form");
  const [winner, setWinner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [models, setModels] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [callMsg, setCallMsg] = useState("");
  const poll = useRef(null);
  const body = () => ({ city: trip.city, from: trip.from, to: trip.to, pickupAt: iso(trip.pickupAt), returnAt: trip.tripType === "ROUND" ? iso(trip.returnAt) : null,
    tripType: trip.tripType, seatClass: seats, model: model.trim() || undefined, lang });

  useEffect(() => {
    if (!seats) return undefined;
    const id = setTimeout(() => {
      apiRequest("/trip-requests/quote", { method: "POST", body: JSON.stringify(body()) })
        .then((r) => { setQuote(r?.data || null); if (!model.trim()) setModels(r?.data?.models || []); }).catch(() => setQuote(null));
    }, model ? 600 : 0);
    return () => clearTimeout(id);
  }, [seats, model]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearInterval(poll.current), []);

  const book = async () => {
    if (!seats) return;
    if (!getUserRole()) { onLogin?.(); return; }
    setBusy(true);
    try {
      const r = await apiRequest("/trip-requests/instant", { method: "POST", body: JSON.stringify(body()) });
      track("instant_booking", { seats, model: !!model.trim(), from: mode });
      if (r?.data?.status !== "OPEN") { setStage("team"); return; }
      setBookingId(r.data.id);
      setStage("waiting");
      const started = Date.now();
      poll.current = setInterval(async () => {
        const s = await apiRequest(`/trip-requests/instant/${encodeURIComponent(r.data.id)}`).catch(() => null);
        const st = s?.data?.status;
        if (st === "TAKEN") { clearInterval(poll.current); setWinner(s.data.winner); setStage("confirmed"); }
        else if (st && st !== "OPEN") { clearInterval(poll.current); setStage("team"); }
        else if (Date.now() - started > 20 * 60000) { clearInterval(poll.current); setStage("team"); }
      }, 5000);
    } catch {
      setStage("error");
    } finally {
      setBusy(false);
    }
  };

  // "Call driver" (mock TripPool_1, 4): our number rings the customer, then the driver.
  const callDriver = async () => {
    setCallMsg("");
    try {
      await apiRequest(`/trip-requests/instant/${encodeURIComponent(bookingId)}/call`, { method: "POST" });
      setCallMsg(t("ib_calling"));
    } catch {
      setCallMsg(t("ib_error"));
    }
  };
  const route = [trip.from, trip.to].filter(Boolean).map((p) => ln(String(p).split(",")[0], "place")).join(" → ");
  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center" role="dialog" aria-modal="true" aria-label={t(mode === "any" ? "ob_title" : "ib_title")}>
      <button type="button" aria-label={t("hm_close")} onClick={stage === "waiting" ? undefined : onClose} className="absolute inset-0 w-full h-full bg-[rgba(15,23,42,0.45)] cursor-default" />
      <div className="relative w-full max-w-lg bg-white rounded-t-[22px] px-4 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]" style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="w-10 h-1 rounded-full bg-[#E2E8F0] mx-auto mb-3" />
        <p className="text-[15px] font-extrabold text-[#0F172A]">{t(mode === "any" ? "ob_title" : "ib_title")}</p>
        <p className="text-[12px] font-bold text-[#334155] mt-0.5">{route}</p>

        {stage === "form" && (
          <>
            <p className="text-[11.5px] text-[#475569] mt-1.5 leading-snug">{t(mode === "any" ? "ib_textAny" : "ib_text")}</p>
            <p className="text-[11.5px] font-bold text-[#334155] mt-3 mb-1.5">{t("ib_seats")} *</p>
            <div className="flex gap-2">
              {SEATS.map(([k, key]) => (
                <button key={k} type="button" onClick={() => setSeats(k)} aria-pressed={seats === k}
                  className={`flex-1 min-h-[42px] rounded-xl text-[12.5px] font-bold border-2 ${seats === k ? "bg-[#F6FAEC] text-[#5E8700] border-[#7BAE00]" : "bg-white text-[#475569] border-[#E5E7EB]"}`}>{t(key)}</button>
              ))}
            </div>
            <p className="text-[11.5px] font-bold text-[#334155] mt-3 mb-1.5">{t("ib_model")}</p>
            {/* Only the models free for this trip, with how many cars (mock TripPool_1, 3). */}
            {models.length ? (
              <div className="flex flex-wrap gap-1.5">
                {[["", t("ib_any"), null], ...models.map((m) => [m.model, ln(m.model, "car"), m.n])].map(([v, label, n]) => (
                  <button key={v || "any"} type="button" onClick={() => setModel(v)} aria-pressed={model === v}
                    className={`min-h-[40px] rounded-xl px-2.5 py-1 text-[12px] font-bold border-2 text-left ${model === v ? "bg-[#F6FAEC] text-[#3F5E00] border-[#7BAE00]" : "bg-white text-[#475569] border-[#E5E7EB]"}`}>
                    {label}{n ? <span className="block text-[10px] font-semibold text-[#94A3B8]">{fillText(t("ib_cars"), { n })}</span> : null}
                  </button>
                ))}
              </div>
            ) : (
              <input value={model} onChange={(e) => setModel(e.target.value)} placeholder={t("ib_modelHint")} maxLength={40}
                className="w-full h-[42px] rounded-xl border border-[#CBD5E1] px-3 text-[13px]" />
            )}
            {quote?.prices && <p className="text-[13px] text-[#0F172A] mt-3">{fillText(t("ib_fare"), { min: rupees(quote.prices[0]), max: rupees(quote.prices[1]) })}</p>}
            <button type="button" onClick={book} disabled={!seats || busy}
              className="w-full min-h-[48px] mt-4 rounded-xl bg-[#7BAE00] text-white text-[13px] font-extrabold disabled:opacity-50">{seats ? t("ib_book") : t("ib_pickSeats")}</button>
          </>
        )}
        {stage === "waiting" && (
          <div className="py-6 text-center">
            <div className="w-8 h-8 mx-auto rounded-full border-[3px] border-[#D6E6B0] border-t-[#7BAE00] animate-spin" />
            <p className="text-[13.5px] font-bold text-[#0F172A] mt-3">{t("ib_finding")}</p>
            <p className="text-[11.5px] text-[#64748B] mt-1 leading-snug">{t("ib_wait")}</p>
          </div>
        )}
        {stage === "confirmed" && (
          <div className="py-4">
            <p className="text-[15px] font-extrabold text-[#166534]">✅ {t("ib_confirmed")}</p>
            {winner && <p className="text-[13px] text-[#0F172A] mt-1">{ln(winner.label, "car")} · ₹{rupees(winner.price)}</p>}
            <p className="text-[12.5px] text-[#334155] mt-1">{t("ib_callYou")}</p>
            {bookingId && (
              <>
                <button type="button" onClick={callDriver} className="w-full min-h-[46px] mt-3 rounded-xl bg-[#7BAE00] text-white text-[13px] font-extrabold">{t("ib_callDriver")}</button>
                <p className="text-[10.5px] text-[#64748B] text-center mt-1">{t("ib_private")}</p>
                {callMsg && <p className="text-[12px] font-semibold text-[#3F6B00] text-center mt-1.5">{callMsg}</p>}
              </>
            )}
            <button type="button" onClick={onClose} className="w-full min-h-[46px] mt-4 rounded-xl border border-[#CBD5E1] text-[13px] font-bold text-[#334155]">{t("wg_done")}</button>
          </div>
        )}
        {(stage === "team" || stage === "error") && (
          <div className="py-4">
            <p className="text-[13.5px] font-bold text-[#0F172A]">{t(stage === "team" ? "ib_team" : "ib_error")}</p>
            <button type="button" onClick={onClose} className="w-full min-h-[46px] mt-4 rounded-xl border border-[#CBD5E1] text-[13px] font-bold text-[#334155]">{t("wg_done")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
